import React, { useMemo } from 'react';
import InfoTooltip from 'components/InfoTooltip/InfoTooltip';
import Points from 'components/Points';
import { OfferLineItem } from 'types/interfaces/tradeOffer';
import { computeCoverage, computeColumnCutoff } from 'utils/offerCoverage';
import {
  OFFER_COVERAGE_TOOLTIP_ESCROW,
  OFFER_COVERAGE_TOOLTIP_ORDERING,
  OFFER_COVERAGE_TOOLTIP_SECOND_LEG,
} from 'utils/fundingCopy';
import {
  allowedConditionsFor,
  buildAcceptedConditionsByHaveRow,
  priceForCondition,
} from 'utils/offerConditions';
import { formatConditionShort } from 'utils/offerErrors';
import { useOfferBuilder, OfferSide } from '../context/OfferBuilderContext';
import { useSendMatchIndex } from '../hooks/useSendMatchIndex';
import { useRemoveOfferLine } from '../hooks/useRemoveOfferLine';
import OfferCardRow from './OfferCardRow';

export interface EmptyColumnGuidance {
  /** Which adjacent source panel fills this column */
  arrowDirection: 'left' | 'right';
  addLine: string;
  /** Restates the ownership relationship — the pairing the layout teaches */
  relationLine: string;
}

interface OfferColumnProps {
  side: OfferSide;
  title: string;
  emptyGuidance: EmptyColumnGuidance;
}

/** A middle column, tinted to its half: magenta = your side (leaving), violet = theirs (arriving) */
const OfferColumn: React.FC<OfferColumnProps> = ({ side, title, emptyGuidance }) => {
  const {
    counterpartyId,
    youSend,
    theySend,
    youSendTotal,
    theySendTotal,
    viewerPoints,
    counterpartyPoints,
    setItemQuantity,
    setItemCondition,
    rejectedHaveId,
  } = useOfferBuilder();

  // Removal (with the un-catalogued undo rules) is shared with the focus grid's
  // "✓ Added" control: see useRemoveOfferLine.
  const handleRemove = useRemoveOfferLine(side);

  const items = side === 'youSend' ? youSend : theySend;
  const total = side === 'youSend' ? youSendTotal : theySendTotal;

  /**
   * The stepper's ceiling: what the sender owns, AND what the recipient's want
   * record still has room for. Owned-only was a second route to the same
   * over-commitment the panel's Add gating prevents — three copies of ONE
   * printing raised to 3 against a want of 1 needs no sibling printings at all.
   *
   * Counts the other lines on the same want, not this one, since this line's
   * quantity is what the stepper is replacing. Lines with no want binding
   * (seeded from a suggestion, or receive-side) keep the owned-only ceiling.
   */
  const maxQuantityFor = (item: OfferLineItem): number => {
    if (item.originatingWantEntryId == null || item.wantEntryQuantity == null) {
      return item.ownedQuantity;
    }
    const spentElsewhere = items.reduce(
      (sum, i) =>
        i.originatingWantEntryId === item.originatingWantEntryId &&
        i.userMTGCardHaveId !== item.userMTGCardHaveId
          ? sum + i.quantity
          : sum,
      0
    );
    return Math.min(item.ownedQuantity, item.wantEntryQuantity - spentElsewhere);
  };

  // The cutoff is a general "whose points run out where" marker, not a me-only
  // concept. EACH column is funded by the RECEIVING party's balance:
  //
  //   They send  → cards arrive to the viewer   → the VIEWER's budget funds them
  //   You send   → cards go to the counterparty → THEIR budget funds them
  //
  // So both columns draw a line, each computed against its own funder, and each
  // independently: one column being short says nothing about the other.
  //
  // Both balances are required either way, because a funder's budget depends on
  // whether they move first or second. When either is unknown we render no
  // divider, no dimming, and no gating — a missing balance must never block a
  // build.
  const fundingKnown = viewerPoints != null && counterpartyPoints != null;

  const funding = useMemo(() => {
    if (!fundingKnown) return null;
    return computeCoverage({
      items: theySend,
      viewerPoints,
      counterpartyPoints,
      youSendTotal,
    });
  }, [fundingKnown, theySend, viewerPoints, counterpartyPoints, youSendTotal]);

  /**
   * This column's funder and their budget. Whoever receives the cards pays for
   * them, and whoever moves SECOND is paid by the first leg before they have to
   * escrow — so each budget can include the value about to arrive.
   */
  const funderBudget = funding
    ? side === 'theySend'
      ? funding.coverageBudget
      : funding.viewerCanOpen
        ? counterpartyPoints! + theySendTotal
        : counterpartyPoints!
    : 0;

  const cutoff = useMemo(() => {
    if (!funding || items.length === 0) return null;
    const result = computeColumnCutoff({ items, funderBudget });
    // Nothing to say when the funder covers the whole column: no divider, no
    // dimming, no success state. The column looks exactly as it does today.
    return result.shortfall > 0 ? result : null;
  }, [funding, items, funderBudget]);

  // Render the cutoff's ordering when there is one (fundable cards first, then
  // unfundable, so the line is a single clean cut), otherwise the plain list.
  const renderItems = (cutoff?.orderedItems as OfferLineItem[] | undefined) ?? items;

  const uncoveredIds = useMemo(() => new Set(cutoff?.uncoveredIds ?? []), [cutoff]);

  // The label names the budget the line is drawn at, in the funder's voice.
  // A budget the user cannot find in a wallet reads as a bug, so when the
  // budget includes value about to arrive, the label says so. The "plus
  // incoming" form is skipped when that incoming value is 0, where naming it
  // would be noise rather than detail.
  const funderBalance = (side === 'theySend' ? viewerPoints : counterpartyPoints) ?? 0;
  const incoming = side === 'theySend' ? youSendTotal : theySendTotal;
  const budgetIncludesIncoming = funderBudget > funderBalance && incoming > 0;
  const owner = side === 'theySend' ? 'Your' : 'Their';
  const dividerLabel = cutoff
    ? budgetIncludesIncoming
      ? `${owner} ${funderBalance.toLocaleString()} plus ${incoming.toLocaleString()} incoming runs out here`
      : `${owner} ${funderBalance.toLocaleString()} runs out here`
    : null;

  // A bold heading, body copy a step dimmer, and the ordering rule ruled off
  // below — the last paragraph explains WHERE the line falls rather than why
  // the limit exists, so it reads as a footnote to the two above it.
  const coverageTooltip = (
    <>
      <p className="info-tooltip-heading">Why the list stops here</p>
      <p className="info-tooltip-body">{OFFER_COVERAGE_TOOLTIP_ESCROW}</p>
      <p className="info-tooltip-body">{OFFER_COVERAGE_TOOLTIP_SECOND_LEG}</p>
      <p className="info-tooltip-note">{OFFER_COVERAGE_TOOLTIP_ORDERING}</p>
    </>
  );

  /**
   * One horizontal cut: everything above is paid for and sendable, everything
   * below is not. The column is ordered by computeColumnCutoff to make that
   * true — a dimmed row must never sit above the line, which is what made the
   * first version read as the whole column being greyed out.
   */
  const renderCoverageDivider = () => (
    <div className="offer-coverage-divider" role="separator" data-testid="coverage-divider">
      {/* The whole label is one hover/focus target — text, icon and padding.
          The dashed rules either side stay non-interactive. */}
      <span className="offer-coverage-divider-label" data-testid="coverage-divider-label">
        <InfoTooltip
          focusable
          variant="panel"
          placement="bottom"
          text={coverageTooltip}
          triggerLabel={<span className="offer-coverage-divider-text">{dividerLabel}</span>}
        />
      </span>
    </div>
  );

  // Live accepted-condition unions from the candidates index (RTK dedupes the
  // underlying queries with SourcePanel's). The dropdown prefers this over the
  // set frozen onto the item at add time: it tracks want-record edits made
  // since the add, and it widens to the accept-if-any union when several want
  // records match the same row (the server rule, kept by ruling 2026-07-30).
  const matchIndex = useSendMatchIndex(counterpartyId, side === 'youSend');
  const liveAcceptedByHaveRow = useMemo(
    () => buildAcceptedConditionsByHaveRow(Array.from(matchIndex.index.values())),
    [matchIndex.index]
  );

  // The condition the sender declares they'll ship — youSend rows only
  // (decision 1: a proposer never sets the condition of the recipient's
  // cards). Renders only for candidates-sourced items carrying the price map;
  // suggestion-seeded bundles stay locked to their validated conditions.
  const renderConditionSelect = (item: OfferLineItem): React.ReactNode => {
    if (side !== 'youSend' || !item.quotedPricesByCondition) return null;
    const liveAccepted = liveAcceptedByHaveRow.get(item.userMTGCardHaveId);
    const allowed = allowedConditionsFor(item.condition, liveAccepted ?? item.acceptedConditions);
    if (allowed.length <= 1) return null;
    const declared = item.declaredCondition ?? item.condition;
    // A since-narrowed want can leave the declared condition outside the live
    // set; keep it visible (selected) so the control never renders blank - the
    // server's create-time validation remains the enforcement point.
    const options = allowed.includes(declared) ? allowed : [declared, ...allowed];
    const priceSource = {
      condition: item.condition,
      quotedPricePoints: item.rowQuotedPricePoints ?? null,
      quotedPricesByCondition: item.quotedPricesByCondition,
    };
    return (
      <select
        className="offer-condition-select"
        aria-label={`Condition of ${item.card.name} you will ship`}
        value={declared}
        onChange={(e) => setItemCondition(item.userMTGCardHaveId, e.target.value)}
      >
        {options.map((c) => {
          const price = priceForCondition(priceSource, c);
          return (
            <option key={c} value={c} disabled={price == null}>
              {formatConditionShort(c)}
              {price == null ? ' · no price' : ''}
            </option>
          );
        })}
      </select>
    );
  };

  return (
    <section
      className={`offer-column ${side === 'youSend' ? 'offer-column--you' : 'offer-column--they'}`}
      aria-label={title}
    >
      <header className="offer-column-header">
        <h3 className="offer-column-title">{title}</h3>
        <div className="offer-column-count">
          {items.length} {items.length === 1 ? 'card' : 'cards'}
        </div>
      </header>
      <div className="offer-column-body">
        {items.length === 0 ? (
          <div className="offer-column-empty">
            <span
              className={`offer-column-empty-arrow offer-column-empty-arrow--${emptyGuidance.arrowDirection}`}
              aria-hidden="true"
            >
              {emptyGuidance.arrowDirection === 'left' ? '←' : '→'}
            </span>
            <p className="offer-column-empty-add">{emptyGuidance.addLine}</p>
            <p className="offer-column-empty-relation">{emptyGuidance.relationLine}</p>
          </div>
        ) : (
          renderItems.map((item, index) => {
            const isUncovered = uncoveredIds.has(item.userMTGCardHaveId);
            // The line sits at the boundary INDEX, between the fundable block
            // and the unfundable one, so it is always a single clean cut.
            const isBoundary = cutoff?.boundaryIndex === index;
            // The repair attaches to the row whose removal actually clears the
            // shortfall, which is NOT always the first uncovered row.
            const isRepairRow =
              cutoff?.removalCandidateId != null &&
              cutoff.removalCandidateId === item.userMTGCardHaveId;
            // The server rejected the offer because of THIS line. Only ever
            // set on a failed submit, and cleared by the next edit.
            const isRejected = rejectedHaveId === item.userMTGCardHaveId;

            return (
              <React.Fragment key={item.userMTGCardHaveId}>
                {isBoundary && renderCoverageDivider()}
                <OfferCardRow
                  card={item.card}
                  foil={item.foil}
                  finish={item.finish}
                  condition={item.declaredCondition ?? item.condition}
                  pricePoints={item.unitPricePoints}
                  quantityBadge={item.quantity}
                  uncovered={isUncovered || isRejected}
                  /* The shortfall is a single total, so it is stated ONCE, on
                     the boundary row. Further uncovered rows dim with no number.
                     A server rejection names ONE line and takes precedence: it
                     is why the submit just failed, and the user is looking for
                     the card to remove. */
                  shortfallNote={
                    isRejected ? (
                      <span
                        className="offer-coverage-shortfall"
                        data-testid="want-quantity-rejected"
                      >
                        More copies than they want
                      </span>
                    ) : isBoundary && cutoff ? (
                      <span className="offer-coverage-shortfall" data-testid="coverage-shortfall">
                        <Points type="text" points={cutoff.shortfall} /> not covered
                      </span>
                    ) : undefined
                  }
                  /* Offered only when the cutoff PROVED a single removal
                     clears the shortfall. When no removal does, there is no
                     footer at all — never a button that leaves the user short. */
                  repairFooter={
                    isRepairRow && cutoff ? (
                      <div className="offer-coverage-repair" data-testid="coverage-repair">
                        <span className="offer-coverage-repair-copy">
                          {cutoff.surplusAfterRemoval === 0 ? (
                            "Remove it and you're exactly covered."
                          ) : (
                            <>
                              Remove it and you&apos;re covered with{' '}
                              <Points type="text" points={cutoff.surplusAfterRemoval ?? 0} /> to
                              spare.
                            </>
                          )}
                        </span>
                        <button
                          type="button"
                          className="offer-coverage-repair-btn"
                          aria-label={`Remove ${item.card.name} to cover this offer`}
                          onClick={() => handleRemove(item)}
                        >
                          Remove
                        </button>
                      </div>
                    ) : undefined
                  }
                  action={
                <div className="offer-line-controls">
                  {renderConditionSelect(item)}
                  {item.ownedQuantity > 1 && (
                    <div className="offer-qty-stepper">
                      <button
                        type="button"
                        aria-label={`Decrease quantity of ${item.card.name}`}
                        disabled={item.quantity <= 1}
                        onClick={() =>
                          setItemQuantity(side, item.userMTGCardHaveId, item.quantity - 1)
                        }
                      >
                        −
                      </button>
                      <span aria-live="polite">{item.quantity}</span>
                      <button
                        type="button"
                        aria-label={`Increase quantity of ${item.card.name}`}
                        disabled={item.quantity >= maxQuantityFor(item)}
                        title={
                          item.quantity >= maxQuantityFor(item) &&
                          maxQuantityFor(item) < item.ownedQuantity
                            ? "They don't want any more copies of this card"
                            : undefined
                        }
                        onClick={() =>
                          setItemQuantity(side, item.userMTGCardHaveId, item.quantity + 1)
                        }
                      >
                        +
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    className="offer-remove-btn"
                    aria-label={`Remove ${item.card.name}`}
                    onClick={() => handleRemove(item)}
                  >
                    ×
                  </button>
                    </div>
                  }
                />
              </React.Fragment>
            );
          })
        )}
      </div>
      <footer className="offer-column-total">
        <span>Total</span>
        <Points type="small" points={total} />
      </footer>
    </section>
  );
};

export default OfferColumn;
